import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';
import './index.css';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin.includes('localhost:3000') ? 'http://localhost:3001' : '');

const UserManagement = ({ onClose }) => {
  const { hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [userForm, setUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    active: true
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`);
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      setError('Erreur lors du chargement des utilisateurs');
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      await axios.post(`${API_URL}/api/auth/register`, userForm);
      setSuccess('Utilisateur créé avec succès!');
      setUserForm({ username: '', email: '', password: '', role: 'user', active: true });
      setShowCreateModal(false);
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const updateData = { ...userForm };
      if (!updateData.password) {
        delete updateData.password;
      }
      await axios.put(`${API_URL}/api/users/${editingUser.id}`, updateData);
      setSuccess('Utilisateur mis à jour avec succès!');
      setEditingUser(null);
      setUserForm({ username: '', email: '', password: '', role: 'user', active: true });
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/users/${id}`);
      setSuccess('Utilisateur supprimé avec succès!');
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      email: user.email,
      password: '',
      role: user.role,
      active: user.active === 1
    });
    setError(null);
    setSuccess(null);
  };

  const closeModal = () => {
    setShowCreateModal(false);
    setEditingUser(null);
    setUserForm({ username: '', email: '', password: '', role: 'user', active: true });
    setError(null);
    setSuccess(null);
  };

  if (!hasPermission('users.view')) {
    return null;
  }

  return (
    <div className="admin-panel">
      <div className="admin-panel-header">
        <h2>Gestion des Utilisateurs</h2>
        <div className="admin-panel-actions">
          {hasPermission('users.manage') && (
            <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>
              + Créer un utilisateur
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>
            Fermer
          </button>
        </div>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="permissions-info">
        <h3>📋 Permissions par rôle</h3>
        <div className="permissions-table">
          <table>
            <thead>
              <tr>
                <th>Permission</th>
                <th>Superadmin</th>
                <th>Admin</th>
                <th>User</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Packs</strong></td>
                <td colSpan="4"></td>
              </tr>
              <tr>
                <td>Voir les packs</td>
                <td>✅</td>
                <td>✅</td>
                <td>✅</td>
                <td>✅</td>
              </tr>
              <tr>
                <td>Créer des packs</td>
                <td>✅</td>
                <td>✅</td>
                <td>✅</td>
                <td>❌</td>
              </tr>
              <tr>
                <td>Modifier des packs</td>
                <td>✅</td>
                <td>✅</td>
                <td>✅</td>
                <td>❌</td>
              </tr>
              <tr>
                <td>Supprimer des packs</td>
                <td>✅</td>
                <td>✅</td>
                <td>✅</td>
                <td>❌</td>
              </tr>
              <tr>
                <td>Voir les statistiques</td>
                <td>✅</td>
                <td>✅</td>
                <td>✅</td>
                <td>✅</td>
              </tr>
              <tr>
                <td><strong>Utilisateurs</strong></td>
                <td colSpan="4"></td>
              </tr>
              <tr>
                <td>Voir les utilisateurs</td>
                <td>✅</td>
                <td>✅</td>
                <td>❌</td>
                <td>❌</td>
              </tr>
              <tr>
                <td>Gérer les utilisateurs</td>
                <td>✅</td>
                <td>❌</td>
                <td>❌</td>
                <td>❌</td>
              </tr>
              <tr>
                <td><strong>Logs</strong></td>
                <td colSpan="4"></td>
              </tr>
              <tr>
                <td>Voir les logs</td>
                <td>✅</td>
                <td>✅</td>
                <td>❌</td>
                <td>❌</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {loading ? (
        <div className="loading">Chargement...</div>
      ) : (
        <div className="users-table">
          <table>
            <thead>
              <tr>
                <th>Nom d'utilisateur</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Statut</th>
                <th>Date de création</th>
                {hasPermission('users.manage') && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id}>
                  <td>{user.username}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge role-${user.role}`}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    <span className={user.active === 1 ? 'status-active' : 'status-inactive'}>
                      {user.active === 1 ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                  {hasPermission('users.manage') && (
                    <td>
                      <button className="btn btn-secondary btn-small" onClick={() => openEditModal(user)}>
                        ✏️ Modifier
                      </button>
                      <button className="btn btn-danger btn-small" onClick={() => handleDelete(user.id)}>
                        🗑️ Supprimer
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(showCreateModal || editingUser) && hasPermission('users.manage') && (
        <div className="modal" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingUser ? 'Modifier l\'utilisateur' : 'Créer un utilisateur'}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>
            <form onSubmit={editingUser ? handleUpdate : handleCreate}>
              <div className="form-group">
                <label>Nom d'utilisateur</label>
                <input
                  type="text"
                  value={userForm.username}
                  onChange={(e) => setUserForm({ ...userForm, username: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Mot de passe {editingUser && '(laisser vide pour ne pas modifier)'}</label>
                <input
                  type="password"
                  value={userForm.password}
                  onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                  required={!editingUser}
                />
              </div>
              <div className="form-group">
                <label>Rôle</label>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({ ...userForm, role: e.target.value })}
                >
                  <option value="view">View</option>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={userForm.active}
                    onChange={(e) => setUserForm({ ...userForm, active: e.target.checked })}
                  />
                  Actif
                </label>
              </div>
              {error && <div className="error">{error}</div>}
              <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: '20px' }}>
                {editingUser ? 'Mettre à jour' : 'Créer'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
